import { Component, OnInit, Output, EventEmitter, Input, HostListener, Inject, LOCALE_ID, booleanAttribute } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { CalendarDateFormatter, CalendarMomentDateFormatter, CalendarView, DateAdapter } from 'angular-calendar';
import { CalendarSchedulerEvent, SchedulerDateFormatter, CalendarSchedulerEventAction, DAYS_IN_WEEK, subPeriod, addPeriod, endOfPeriod, startOfPeriod, SchedulerViewDay, SchedulerViewHour, SchedulerViewHourSegment, SchedulerEventTimesChangedEvent } from 'angular-calendar-scheduler';

import { CalendarService } from '../services/calendar.service';
import { WorkOrder } from '../../shared/models/work.order.interface';
import { colors, EventColor } from '../../shared/constants/colors';
import { DateService } from '../../shared/services/date.service';
import { Resource } from '../../shared/models/resource.interface';
import { Crew } from '../../shared/models/crew.interface';
import { CalendarFilter } from '../models/calendar.filter';
import { RolePermission } from '../../shared/models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { WorkOrderService } from '../../work-order/services/work.order.service';
import { ResourceTypeEdit } from '../../work-order/list/list.component';
import { ResourceEdit } from '../../work-order/models/resource.edit.interface';
import { WorkOrderType } from '../../shared/models/work.order.type.interface';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { WorkOrderEdit } from '../../job/models/work.order.edit.interface';
import { JobAreaSummary } from '../../job/models/job.area.summary.interface';
import { JobService } from '../../job/services/job.service';
import { FieldTechnician } from '../../shared/models/field.technician.interface';
import { Job } from '../../shared/models/job.interface';
import { Appointment } from '../../shared/models/appointment.interface';
import { NgForm } from '@angular/forms';
import * as moment from 'moment';
import { WorkOrderPrintDialog } from '../../work-order/work-order-print-dialog/work-order-print-dialog.component';
import { DealerService } from '../../dealer/services/dealer.service';
import { Dealer } from '../../shared/models/dealer.interface';
import { QuotesDataSource } from '../../quote/datasources/quotes-data-source';
import { Quote } from '../../shared/models/quote.interface';
import { UserService } from '../../user/services/user.service';
import { AuthService } from '../../shared/services/auth.service';
import { storageVariableNames } from '../../shared/constants/storageVariableNames';

export interface CalendarResourceDialogData {
  workOrder: WorkOrderEdit;

  resourceTypes: ResourceTypeEdit[];
}
export interface CalendarCrewDialogData {
  workOrder: WorkOrderEdit;

  crews: Crew[];
  fieldTechs: FieldTechnician[];
}

export interface ProjectsData {
  quoteId: number;
  quoteName: string;
}

export interface CalendarAppointmentDialogData {
  appointment: Appointment;
  startTime: string;
  stopTime: string;

  readOnly: boolean;
  times: string[];
}
export interface CalendarEditDialogData {
  crews: Crew[];
  workOrderTypes: WorkOrderType[];
  resourceTypes: ResourceTypeEdit[];
  workOrder: WorkOrderEdit;
  startTime: string;
  stopTime: string;

  readOnly: boolean;
  times: string[];
}

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  providers: [
    {
      provide: CalendarDateFormatter, useClass: CalendarMomentDateFormatter
    },
    {
      provide: CalendarDateFormatter,
      useClass: SchedulerDateFormatter
    }]
})
export class EditComponent extends AbstractBiditComponent implements OnInit {
  view: CalendarView = CalendarView.Week;
  viewDate: Date = new Date();
  viewDays: number = DAYS_IN_WEEK;
  refresh: Subject<any> = new Subject();
  locale: string = 'en';
  weekStartsOn: number = 0; // sunday
  startsWithToday: boolean = true;
  excludeDays: number[] = []; // [0];
  hourSegments: number = 2;
  dayStartHour: number = 6;
  dayEndHour: number = 22;

  isTimeOfDayVisible: boolean = false;

  dayStartMinutes: number = 0;
  dayEndMinutes: number = 0;

  minDate: Date = new Date(2000, 1, 1);
  maxDate: Date = new Date(2200, 1, 1);
  prevBtnDisabled: boolean = false;
  nextBtnDisabled: boolean = false;

  filter: CalendarFilter = { crew: '' as any, quoteId: null, viewMode: '' } as CalendarFilter;

  dealer: Dealer;
  crews: Crew[];
  fieldTechs: FieldTechnician[];
  workOrderTypes: WorkOrderType[];
  resources: Resource[];
  projects: ProjectsData[] = [];
  workOrders: WorkOrder[];
  appointments: Appointment[];
  events: CalendarSchedulerEvent[];
  loadProjectList: boolean = true;

  submitted = false;
  canEdit: boolean;

  times: string[];

  private usernameSubscription: Subscription;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.adjustViewDays();
  }

  constructor(@Inject(LOCALE_ID) locale: string,
    deviceDetectorService: DeviceDetectorService,
    private datepipe: DatePipe, private dateAdapter: DateAdapter, private dialog: MatDialog,
    private dateService: DateService,
    private calendarService: CalendarService,
    private workOrderService: WorkOrderService,
    private dealerService: DealerService,
    private userService: UserService) {

    super(deviceDetectorService);
    this.locale = locale;
  }

  ngOnInit() {
    this.canEdit = CalendarService.hasPermission(RolePermission.EditWorkOrder);

    this.usernameSubscription = AuthService.authNavStatus$.subscribe(authenticated => {
      this.filter.viewMode = "";
      if (authenticated) {
        let user = AuthService._authUserSource.value;
        if (sessionStorage.getItem(storageVariableNames.calendarView) === null) {
          this.setSessionVariable(storageVariableNames.calendarView, user.calendarView);
        }

        this.filter.viewMode = sessionStorage.getItem(storageVariableNames.calendarView);
        this.setView();
      }
    });

    this.dealerService.getDealerDetails()
      .subscribe({
        next: (data) => {
          this.dealer = data
          this.setCalendarStartStop();
          this.loadCalendar();
          this.adjustViewDays();
        },
        error: (error: string) => this.onError(error)
      });

    this.workOrderService.getCrews()
      .subscribe({
        next: (data) => this.crews = data,
        error: (error: string) => this.onError(error)
      });

    this.workOrderService.getFieldTechs()
      .subscribe({
        next: (data) => this.fieldTechs = data,
        error: (error: string) => this.onError(error)
      });

    this.workOrderService.getResources()
      .subscribe({
        next: (data) => this.resources = data,
        error: (error: string) => this.onError(error)
      });

    this.workOrderService.getWorkOrderTypes()
      .subscribe({
        next: (data) => this.workOrderTypes = data,
        error: (error: string) => this.onError(error)
      });
  }

  ngOnDestroy() {
    this.usernameSubscription.unsubscribe();
  }

  setSessionVariable(variableName: string, variableValue: any) {
    sessionStorage.setItem(variableName, variableValue);
  }

  setCalendarStartStop() {
    let startHour = this.dealer?.defaultWorkOrderStartTime ? this.dateService.getTimeHours(this.dealer.defaultWorkOrderStartTime) : 6;
    let stopHour = this.dealer?.defaultWorkOrderStopTime ? this.dateService.getTimeHours(this.dealer.defaultWorkOrderStopTime) : 22;

    this.dayStartMinutes = this.dealer?.defaultWorkOrderStartTime ? this.dateService.getTimeMinutes(this.dealer.defaultWorkOrderStartTime) : 0;
    this.dayEndMinutes = this.dealer?.defaultWorkOrderStopTime ? this.dateService.getTimeMinutes(this.dealer.defaultWorkOrderStopTime) : 0;

    this.dayStartHour = startHour;
    this.dayEndHour = stopHour - 1;

    this.loadCalendar();
    this.setTimesData();
  }

  setTimesData() {
    this.times = new Array();
    for (let hour = this.dayStartHour ?? 6; hour < this.dayEndHour + 1 ?? 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        let timeValue = new Date(1970, 0, 1, hour, minute, 0);
        this.times.push(this.datepipe.transform(timeValue, 'hh:mm a'));
      }
    }
    let lastTimeValue = new Date(1970, 0, 1, this.dayEndHour + 1, this.dayEndMinutes, 0);
    this.times.push(this.datepipe.transform(lastTimeValue, 'hh:mm a'));
  }

  loadCalendar() {
    this.isRequesting = true;

    this.calendarService.getCalendar(this.viewDate, this.view, this.filter)
      .subscribe({
        next: (data) => {
          this.workOrders = data.workOrders;
          this.appointments = data.appointments;

          if (this.loadProjectList) {
            this.projects = [];

            this.workOrders.forEach(p => {
              let projectData: ProjectsData;
              projectData = { quoteId: p.project.quote_ID, quoteName: p.project.quote_Name };
              this.projects.findIndex(p => p.quoteId === projectData.quoteId && p.quoteName === projectData.quoteName) === -1 ?
                this.projects.push(projectData) : '';
            });

            this.loadProjectList = false;
          }

          //let expandedAppointments = this.adjustAppointmentMultidayTimes(data.appointments);
          let expandedEvents = this.excludeWeekends(data.workOrders);

          let woEvents = expandedEvents.map((wo: WorkOrder): CalendarSchedulerEvent => {
            let color: EventColor = (<any>Object).assign({}, this.filter.viewMode == 'Month' ? colors.yellow : colors.white) as EventColor;

            if (wo.workOrderCrews != null && wo.workOrderCrews.length == 1) {
              color.primary = wo.workOrderCrews[0].crew.crew_Color;
            }
            if (wo.workOrder_Color && wo.workOrder_Color.toLowerCase() != '#ffffff') {
              color.primary = wo.workOrder_Color;
            }
            let start = new Date(wo.workOrder_Start);
            let end = new Date(wo.workOrder_Stop);

            return {
              id: `W|${wo.workOrder_ID}`,
              title: wo.project.quote_Name + '-' + wo.workOrder_Seq + '-' + wo.workOrderType.workOrderType_Desc, //wo.workOrderType.workOrderType_Desc,
              content: wo.project.quote_Name + '-' + wo.workOrder_Seq + '-' + wo.workOrderType.workOrderType_Desc,
              color: color,
              start: start,
              end: end,
              isClickable: true,
              draggable: true,
              actions: []
            } as CalendarSchedulerEvent;
          });
          let apptEvents = this.appointments.map((appt: Appointment): CalendarSchedulerEvent => {
            let color: EventColor = (<any>Object).assign({}, this.filter.viewMode == 'Month' ? colors.yellow : colors.white) as EventColor;

            if (appt.appointment_Color) {
              color.primary = appt.appointment_Color;
            }
            let start = new Date(appt.appointment_Start);
            let end = new Date(appt.appointment_Stop);

            return {
              id: `A|${appt.appointment_ID}`,
              title: appt.appointment_Description,
              content: appt.appointment_Instructions,
              color: color,
              start: start,
              end: end,
              isClickable: true,
              draggable: true,
              actions: []
            } as CalendarSchedulerEvent;
          });

          this.events = woEvents.concat(apptEvents);

          this.isRequesting = false;
        },
        error: (error: string) => this.onError(error)
      });
  }
  excludeWeekends(workOrders: WorkOrder[]): WorkOrder[] {
    let result = [];

    workOrders.forEach(wo => {
      let start = new Date(wo.workOrder_Start);
      let end = new Date(wo.workOrder_Stop);

      let jobStart = new Date(start);

      if (this.sameDay(start, end)) {
        result.push(wo);
        return;
      }

      let date = start;

      while (!this.sameDay(end, date)) {

        if (!wo.workOrder_IncludeWeekends && (date.getDay() === 6 || date.getDay() === 0)) {
          date = this.addDays(date, 1);
          continue;
        }

        let dayEnd = new Date(date);

        //if (!this.sameDay(end, date)) {
        let clone = Object.assign({}, wo);

        if (!wo.useSameTimesMultiDay) {
          dayEnd = new Date(date);
          dayEnd.setHours(this.dayEndHour + 1);
        }
        else {
          dayEnd = new Date(date);
          dayEnd.setHours(end.getHours(), end.getMinutes(), end.getSeconds());
        }

        let startDate = this.datepipe.transform(date, 'yyyy-MM-ddTHH:mm:ss');
        let endDate = this.datepipe.transform(dayEnd, 'yyyy-MM-ddTHH:mm:ss');

        clone.workOrder_Start = startDate;
        clone.workOrder_Stop = endDate;

        result.push(clone);

        // if using same times for each day...then set the start day hours to the
        // dealer start hours to make it look continuous
        if (!wo.useSameTimesMultiDay) {
          date.setHours(this.dayStartHour, 0);
        }

        date = this.addDays(date, 1);
        //}
      }

      let clone = Object.assign({}, wo);
      clone.workOrder_Start = this.datepipe.transform(date, 'yyyy-MM-ddTHH:mm:ss');
      clone.workOrder_Stop = this.datepipe.transform(end, 'yyyy-MM-ddTHH:mm:ss');
      result.push(clone);

    });

    return result;
  }

  sameDay(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  addDays(date: Date, days: number): Date {
    let result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  adjustViewDays(): void {
    if (this.filter.viewMode == 'Day') {
      this.viewDays = 1;
      return;
    }

    const currentWidth: number = window.innerWidth;
    if (currentWidth <= 550) {
      this.viewDays = 1;
    } else if (currentWidth <= 768) {
      this.viewDays = 3;
    } else {
      this.viewDays = DAYS_IN_WEEK;
    }
  }

  changeDate(date: Date): void {
    console.log('changeDate', date);
    this.viewDate = date;
    this.dateOrViewChanged();
  }

  includesWeekend(workOrder: WorkOrder, startDate: Date, endDate: Date) {
    let hasWeekendDays: boolean = false;


    if (workOrder.workOrder_IncludeWeekends) {
      hasWeekendDays = false;
    }

    let date = startDate;

    while (!this.sameDay(date, endDate)) {
      if ((date.getDay() === 6 || date.getDay() === 0)) {
        hasWeekendDays = true;
        break;
      }
      else {
        date = this.addDays(date, 1);
        continue;
      }
    }

    if (this.sameDay(date, endDate) && (date.getDay() === 6 || date.getDay() === 0)) {
      hasWeekendDays = true;
    }

    return hasWeekendDays;
  }

  confirmWeekendOK(event: CalendarSchedulerEvent, workOrder: WorkOrder) {

    this.showDialog('Work order indicates weekends are not included. New Dates include weekends, would you like to continue?', 'Confirm Work Order Change', 'Yes', 'No')
      .subscribe((data) => {
        if (!data) {
          this.loadCalendar();
        }
        else {
          workOrder.workOrder_Start = this.datepipe.transform(event.start, 'yyyy-MM-ddTHH:mm:ss');
          workOrder.workOrder_Stop = this.datepipe.transform(event.end, 'yyyy-MM-ddTHH:mm:ss');
          this.updateWorkOrderDates(workOrder);
        }
      });
  }

  changeView(): void {
    this.setView();
    this.updateCalendarView();

  }

  setView() {
    switch (this.filter.viewMode) {
      case 'Month':
        this.view = CalendarView.Month;
        break;
      case 'Week':
        this.view = CalendarView.Week;
        break;
      default:
        this.view = CalendarView.Day;
        break;
    }
  }

  previousView(): void {
    if (this.view == CalendarView.Day) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth(),
        this.viewDate.getDate() - 1);
    } else if (this.view == CalendarView.Week) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth(),
        this.viewDate.getDate() - this.viewDays);
    } else {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() - 1,
        this.viewDate.getDate());
    }

    this.loadProjectList = true;
    this.dateOrViewChanged();
  }

  nextView(): void {
    if (this.view == CalendarView.Day) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth(),
        this.viewDate.getDate() + 1);
    } else if (this.view == CalendarView.Week) {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth(),
        this.viewDate.getDate() + this.viewDays);
    } else {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() + 1,
        this.viewDate.getDate());
    }

    this.loadProjectList = true;
    this.dateOrViewChanged();
  }

  dateOrViewChanged(): void {
    if (this.startsWithToday) {
      this.prevBtnDisabled = !this.isDateValid(subPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, this.viewDate, 1));
      this.nextBtnDisabled = !this.isDateValid(addPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, this.viewDate, 1));
    } else {
      this.prevBtnDisabled = !this.isDateValid(endOfPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, subPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, this.viewDate, 1)));
      this.nextBtnDisabled = !this.isDateValid(startOfPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, addPeriod(this.dateAdapter, CalendarView.Day/*this.view*/, this.viewDate, 1)));
    }

    if (this.viewDate < this.minDate) {
      this.changeDate(this.minDate);
      return;
    } else if (this.viewDate > this.maxDate) {
      this.changeDate(this.maxDate);
      return;
    }

    this.refresh.next(null);
    this.loadCalendar();
  }

  private isDateValid(date: Date): boolean {
    return /*isToday(date) ||*/ date >= this.minDate && date <= this.maxDate;
  }

  dayHeaderClicked(day: SchedulerViewDay): void {
    console.log('dayHeaderClicked Day', day);
  }

  hourClicked(hour: SchedulerViewHour): void {
    console.log('hourClicked Hour', hour);
  }

  segmentClicked(action: string, segment: SchedulerViewHourSegment): void {
    console.log('segmentClicked Action', action);
    console.log('segmentClicked Segment', segment);
  }

  eventClicked(action: string, $event: any): void {
    let event = $event.event as CalendarSchedulerEvent;
    console.log('eventClicked Action', action);
    console.log('eventClicked Event', event);

    let eventTokens = event.id.split('|');
    let eventId = parseInt(eventTokens[1]);
    if (eventTokens[0] == 'W') {
      let workOrder = this.workOrders.find(wo => wo.workOrder_ID == eventId);
      if (workOrder != null) this.editWorkOrder(workOrder);
    } else if (eventTokens[0] == 'A') {
      let appointment = this.appointments.find(app => app.appointment_ID == eventId);
      if (appointment != null) this.editAppointment(appointment);
    }
  }

  eventTimesChanged({ event, newStart, newEnd, type }: SchedulerEventTimesChangedEvent): void {
    console.log('eventTimesChanged Type', type);
    console.log('eventTimesChanged Event', event);
    console.log('eventTimesChanged New Times', newStart, newEnd);
    const ev: CalendarSchedulerEvent = this.events.find(e => e.id === event.id);

    let origEventStart = event.start;
    //let origEventEnd = event.end;

    ev.start = newStart;
    ev.end = newEnd;
    this.saveEventDates(ev, origEventStart);
  }

  saveEventDates(event, origEventStart) {
    let eventTokens = event.id.split('|');
    let eventId = parseInt(eventTokens[1]);
    if (eventTokens[0] == 'W') {
      let workOrder = this.workOrders.find(wo => wo.workOrder_ID == eventId);

      if (workOrder != null) {
        event = this.adjustEventDates(event, origEventStart, workOrder);
        if (this.includesWeekend(workOrder, event.start, event.end)) {
          this.confirmWeekendOK(event, workOrder);
          return;
        }

        workOrder.workOrder_Start = this.datepipe.transform(event.start, 'yyyy-MM-ddTHH:mm:ss');
        workOrder.workOrder_Stop = this.datepipe.transform(event.end, 'yyyy-MM-ddTHH:mm:ss');
        this.updateWorkOrderDates(workOrder);
      }
    }
    else if (eventTokens[0] == 'A') {
      let appointment = this.appointments.find(app => app.appointment_ID == eventId);
      appointment
      if (appointment != null) {
        appointment.appointment_Start = this.datepipe.transform(event.start, 'yyyy-MM-ddTHH:mm:ss');
        appointment.appointment_Stop = this.datepipe.transform(event.end, 'yyyy-MM-ddTHH:mm:ss');
        this.updateAppointmentDates(appointment);
      }
    }
  }

  updateWorkOrderDates(workOrder: WorkOrder): void {
    this.isRequesting = true;
    this.workOrderService.saveWorkOrder(workOrder.workOrder_ID, workOrder)
      .subscribe({
        next: (data) => {
          this.isRequesting = false;
          this.refresh.next(null);
          this.loadCalendar();
        },
        error: (error: string) => this.onError(error)
      });
  }

  updateAppointmentDates(appointment: Appointment): void {
    this.isRequesting = true;
    this.workOrderService.saveAppointment(appointment.appointment_ID, appointment)
      .subscribe({
        next: (data) => {
          this.isRequesting = false;
          this.refresh.next(null);
          this.loadCalendar();
        },
        error: (error: string) => this.onError(error)
      });
  }


  updateCalendarView(): void {
    this.isRequesting = true;
    this.userService.updateCalendarView(this.filter.viewMode)
      .subscribe({
        next: () => {
          this.loadProjectList = true;
          this.adjustViewDays();
          this.setSessionVariable(storageVariableNames.calendarView, this.filter.viewMode);
          this.dateOrViewChanged();
        },
        error: (error: string) => this.onError(error)
      });
  }

  adjustEventDates(event: CalendarSchedulerEvent, origEventStart: Date, workOrder: WorkOrder): CalendarSchedulerEvent {

    let start = new Date(workOrder.workOrder_Start);
    let end = new Date(workOrder.workOrder_Stop);

    if (!this.sameDay(start, end)) {
      let firstDate = moment(event.start);
      let secondDate = moment(origEventStart);
      console.log(`firstDate ${firstDate}  secondDate ${secondDate}`);

      let diffInDays = firstDate.diff(secondDate, 'days');
      //event.start = origEvent.start;
      //event.end = origEvent.end;

      console.log(`Date Diff ${diffInDays}`);

      let newStart = this.addDays(start, diffInDays);
      let newEnd = this.addDays(end, diffInDays);
      console.log(`newStart ${newStart}  newEnd ${newEnd}`);


      event.start = newStart;
      event.end = newEnd;

      return event;
    }

    return event;
  }

  editWorkOrder(workOrder: WorkOrder) {
    let resourceTypes = this.workOrderService.parseResourceTypes(workOrder, this.resources);

    if (!workOrder.workOrder_Color) workOrder.workOrder_Color = '#FFFFFF';

    let dialogData = {
      crews: this.crews,
      workOrderTypes: this.workOrderTypes,
      resourceTypes: resourceTypes,
      workOrder: workOrder,

      readOnly: !this.canEdit,
      times: this.times
    } as CalendarEditDialogData;

    const dialogRef = this.dialog.open(CalendarEditDialog, {
      width: '710px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result == 'RESOURCE') {
        this.editWorkOrderResources(workOrder);
        return;
      } else if (result == 'CREW') {
        this.editWorkOrderCrew(workOrder);
        return;
      } else if (result) {
        this.loadCalendar();
      }
    });
  }

  addAppointment() {
    let appointment = {} as Appointment;
    this.editAppointment(appointment);
  }

  editAppointment(appointment: Appointment) {
    if (!appointment.appointment_Color) appointment.appointment_Color = '#FFFFFF';

    let dialogData = {
      appointment: appointment,

      readOnly: !this.canEdit,
      times: this.times
    } as CalendarAppointmentDialogData;

    const dialogRef = this.dialog.open(CalendarAppointmentDialog, {
      width: '700px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadCalendar();
      }
    });
  }

  editWorkOrderResources(workOrder: WorkOrder) {
    let resourceTypes = this.workOrderService.parseResourceTypes(workOrder, this.resources);

    const dialogRef = this.dialog.open(CalendarResourceDialog, {
      width: '750px',
      data: {
        workOrder: workOrder,
        resourceTypes: resourceTypes
      } as CalendarResourceDialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadCalendar();
      }
    });
  }

  editWorkOrderCrew(workOrder: WorkOrder) {
    const dialogRef = this.dialog.open(CalendarCrewDialog, {
      width: '750px',
      data: {
        workOrder: workOrder,
        crews: this.crews,
        fieldTechs: this.fieldTechs
      } as CalendarCrewDialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadCalendar();
      }
    });
  }

  printWorkOrders() {

    const dialogRef = this.dialog.open(WorkOrderPrintDialog, {
      width: '850px',
      data: {
        workOrders: this.workOrders,
        showPrintAttachments: false,
        printingFromCalendar: true,
        viewDate: this.viewDate,
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
    });

  }
}

@Component({
  selector: 'calendar-edit-dialog',
  templateUrl: 'calendar.edit.dialog.html',
})
export class CalendarEditDialog extends AbstractBiditComponent implements OnInit {

  job: Job = {} as any;

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private jobService: JobService, private workOrderService: WorkOrderService,
    public dialogRef: MatDialogRef<CalendarEditDialog>,
    private datepipe: DatePipe, private dateService: DateService,
    @Inject(MAT_DIALOG_DATA) public data: CalendarEditDialogData) {
    super(deviceDetectorService);
  }

  ngOnInit() {
    this.selectedCrews = this.data.crews.filter(c => this.data.workOrder.workOrderCrews.find(woc => c.crew_ID == woc.crew_ID) != null);

    if (this.data.workOrder.workOrder_Start) this.data.startTime = this.datepipe.transform(this.data.workOrder.workOrder_Start, 'hh:mm a');
    if (this.data.workOrder.workOrder_Stop) this.data.stopTime = this.datepipe.transform(this.data.workOrder.workOrder_Stop, 'hh:mm a');

    this.jobService.getJob(this.data.workOrder.quote_ID)
      .subscribe({
        next: (data) => {
          this.isRequesting = false;

          this.job = data.job;
          this.workOrderAreas = [];

          let workOrder = data.job.workOrders.find(wo => wo.workOrder_ID == this.data.workOrder.workOrder_ID);

          for (let area of data.jobAreaSummaries) {
            let jobArea = {
              area_Name: area.area_Name,
              quoteAreas: new Array()
            } as JobAreaSummary;

            for (let quoteArea of area.quoteAreas) {
              let existingArea = workOrder.workOrderAreas.find(woa => woa.quoteArea_ID == quoteArea.quoteArea_ID);
              if (existingArea != null) jobArea.quoteAreas.push(quoteArea);
            }

            if (jobArea.quoteAreas.length > 0) this.workOrderAreas.push(jobArea);
          }
        }
      });
  }

  workOrderAreas: JobAreaSummary[];
  selectedCrews: Crew[];
  timeAlert: string;

  checkStartValid(): void {
    let startDate = new Date(this.data.workOrder.workOrder_Start);
    let stopDate = new Date(this.data.workOrder.workOrder_Stop);
    let minDate = new Date('1/1/2000');

    if (startDate < minDate) {
      this.data.workOrder.workOrder_Start = '';
      return;
    }

    this.timeAlert = this.dateService.validateDates(startDate, this.data.startTime, stopDate, this.data.stopTime);
  }

  checkStopValid(): void {
    let startDate = new Date(this.data.workOrder.workOrder_Start);
    let stopDate = new Date(this.data.workOrder.workOrder_Stop);
    let minDate = new Date('1/1/2000');

    if (stopDate < minDate) {
      this.data.workOrder.workOrder_Stop = '';
      return;
    }

    this.timeAlert = this.dateService.validateDates(startDate, this.data.startTime, stopDate, this.data.stopTime);
  }

  updateWorkOrder(form: NgForm): void {
    this.timeAlert = null;

    this.checkStartValid();
    this.checkStopValid();

    if (this.timeAlert == null && form.valid) {
      this.data.workOrder.crew_IDs = this.selectedCrews.map<number>(c => c.crew_ID);

      let selections: ResourceEdit[] = new Array();
      this.data.resourceTypes.forEach(function (resourceType) {
        selections = selections.concat(resourceType.resources.filter((r) => r.isSelected));
      });

      this.data.workOrder.workOrder_Start = this.dateService.combineDateString(this.data.workOrder.workOrder_Start, this.data.startTime);
      this.data.workOrder.workOrder_Stop = this.dateService.combineDateString(this.data.workOrder.workOrder_Stop, this.data.stopTime);

      this.isRequesting = true;
      this.workOrderService.saveWorkOrder(this.data.workOrder.workOrder_ID, this.data.workOrder)
        .subscribe({
          next: (data) => this.dialogRef.close(data),
          error: (error: string) => this.onError(error)
        });
    }
  }

  editResources(): void {
    this.dialogRef.close('RESOURCE');
  }

  customizeCrew(): void {
    this.dialogRef.close('CREW');
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}

@Component({
  selector: 'calendar-appointment-dialog',
  templateUrl: 'calendar.appointment.dialog.html',
})
export class CalendarAppointmentDialog extends AbstractBiditComponent implements OnInit {

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private workOrderService: WorkOrderService,
    public dialogRef: MatDialogRef<CalendarAppointmentDialog>,
    private datepipe: DatePipe, private dateService: DateService,
    @Inject(MAT_DIALOG_DATA) public data: CalendarAppointmentDialogData) {
    super(deviceDetectorService);
  }

  ngOnInit() {
    if (this.data.appointment.appointment_Start) this.data.startTime = this.datepipe.transform(this.data.appointment.appointment_Start, 'hh:mm a');
    if (this.data.appointment.appointment_Stop) this.data.stopTime = this.datepipe.transform(this.data.appointment.appointment_Stop, 'hh:mm a');
  }

  workOrderAreas: JobAreaSummary[];
  selectedCrews: Crew[];
  timeAlert: string;

  checkStartValid(): void {
    let startDate = new Date(this.data.appointment.appointment_Start);
    let stopDate = new Date(this.data.appointment.appointment_Stop);
    let minDate = new Date('1/1/2000');

    if (startDate < minDate) {
      this.data.appointment.appointment_Start = '';
      return;
    }

    this.timeAlert = this.dateService.validateDates(startDate, this.data.startTime, stopDate, this.data.stopTime);
  }

  checkStopValid(): void {
    let startDate = new Date(this.data.appointment.appointment_Start);
    let stopDate = new Date(this.data.appointment.appointment_Stop);
    let minDate = new Date('1/1/2000');

    if (stopDate < minDate) {
      this.data.appointment.appointment_Stop = '';
      return;
    }

    this.timeAlert = this.dateService.validateDates(startDate, this.data.startTime, stopDate, this.data.stopTime);
  }

  updateAppointment(form: NgForm): void {
    this.timeAlert = null;

    this.checkStartValid();
    this.checkStopValid();

    if (this.timeAlert == null && form.valid) {

      this.data.appointment.appointment_Start = this.dateService.combineDateString(this.data.appointment.appointment_Start, this.data.startTime);
      this.data.appointment.appointment_Stop = this.dateService.combineDateString(this.data.appointment.appointment_Stop, this.data.stopTime);

      this.isRequesting = true;
      this.workOrderService.saveAppointment(this.data.appointment.appointment_ID, this.data.appointment)
        .subscribe({
          next: (data) => this.dialogRef.close(data),
          error: (error: string) => this.onError(error)
        });
    }
  }

  deleteAppointment(): void {
    var proceed = confirm($localize`Delete this appointment?`);
    if (!proceed) return;

    this.isRequesting = true;
    this.workOrderService.deleteAppointment(this.data.appointment.appointment_ID)
      .subscribe({
        next: (data) => this.dialogRef.close(data),
        error: (error: string) => this.onError(error)
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}

@Component({
  selector: 'calendar-resource-dialog',
  templateUrl: 'calendar.resource.dialog.html',
})
export class CalendarResourceDialog extends AbstractBiditComponent {

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private workOrderService: WorkOrderService,
    public dialogRef: MatDialogRef<CalendarResourceDialog>,
    @Inject(MAT_DIALOG_DATA) public data: CalendarResourceDialogData) {
    super(deviceDetectorService);
  }

  updateResource(form: NgForm): void {
    if (form.valid) {
      let selections: ResourceEdit[] = new Array();
      this.data.resourceTypes.forEach(function (resourceType) {
        selections = selections.concat(resourceType.resources.filter((r) => r.isSelected));
      });

      this.isRequesting = true;
      this.workOrderService.saveResources(this.data.workOrder.workOrder_ID, selections)
        .subscribe({
          next: (data) => this.dialogRef.close(data),
          error: (error: string) => this.onError(error)
        });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}

@Component({
  selector: 'calendar-crew-dialog',
  templateUrl: 'calendar.crew.dialog.html',
})
export class CalendarCrewDialog extends AbstractBiditComponent implements OnInit {
  workOrderPersonnel: FieldTechnician[] = [];
  availablePersonnel: FieldTechnician[] = [];
  removals: FieldTechnician[] = [];
  additions: FieldTechnician[] = [];

  removeSelection: FieldTechnician;
  addSelection: FieldTechnician;

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private workOrderService: WorkOrderService,
    public dialogRef: MatDialogRef<CalendarCrewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: CalendarCrewDialogData) {
    super(deviceDetectorService);
  }

  ngOnInit() {
    let crews = this.data.crews;
    let wop: FieldTechnician[] = [];
    let available: FieldTechnician[] = [];
    let remove: FieldTechnician[] = [];
    let add: FieldTechnician[] = [];

    // get list of people in associated crews
    this.data.workOrder.workOrderCrews.forEach(function (woc) {
      let crew = crews.find(c => c.crew_ID == woc.crew_ID);
      if (crew != null) wop = wop.concat(crew.crewPersonnel.map(cp => cp.fieldTechnician));
    });


    // get list of people not a part of those crews
    this.data.fieldTechs.forEach(function (ft) {
      let current = wop.find(c => c.fieldTechnician_ID == ft.fieldTechnician_ID);
      if (current == null) available.push(ft);
    });

    this.data.workOrder.workOrderCrewOverrides.forEach(function (woco) {
      if (woco.override_IsRemoval) remove.push(woco.overrideUser);
      else add.push(woco.overrideUser);
    });

    this.workOrderPersonnel = wop;
    this.availablePersonnel = available;
    this.removals = remove;
    this.additions = add;
  }

  updateCrew(form: NgForm): void {
    if (form.valid) {
      let selections: ResourceEdit[] = new Array();

      this.isRequesting = true;
      this.workOrderService.saveCrew(this.data.workOrder.workOrder_ID,
        this.removals.map(r => r.fieldTechnician_ID), this.additions.map(r => r.fieldTechnician_ID))
        .subscribe({
          next: (data) => this.dialogRef.close(data),
          error: (error: string) => this.onError(error)
        });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  removePersonnel(): void {
    if (this.removeSelection != null) {
      let currRemoval = this.removals.find(r => r.fieldTechnician_ID == this.removeSelection.fieldTechnician_ID);
      if (currRemoval == null) this.removals.push(this.removeSelection);
    }
  }

  addPersonnel(): void {
    if (this.addSelection != null) {
      let currAddition = this.additions.find(r => r.fieldTechnician_ID == this.addSelection.fieldTechnician_ID);
      if (currAddition == null) this.additions.push(this.addSelection);
    }
  }

  cancelRemoval(user: FieldTechnician): void {
    let index = this.removals.indexOf(user);
    if (index > -1) {
      this.removals.splice(index, 1);
    }
  }

  cancelAddition(user: FieldTechnician): void {
    let index = this.additions.indexOf(user);
    if (index > -1) {
      this.additions.splice(index, 1);
    }

  }
}
